import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserContext } from "./UserContext";
import { API_BASE_URL } from "../config/api";
import Navbar from "./Navbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faLock,
  faArrowRight,
  faSpinner,
  faExclamationCircle,
  faUserCheck
} from "@fortawesome/free-solid-svg-icons";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { setUser } = useUserContext();

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.token);
        localStorage.setItem("email", email);

        const userDetailsResponse = await fetch(
          `${API_BASE_URL}/api/users/details?email=${email}`
        );

        if (userDetailsResponse.ok) {
          const ud = await userDetailsResponse.json();
          localStorage.setItem("name", ud["username"]);
          localStorage.setItem("id", ud["id"]);
          setUser({ name: ud["username"], email: email, id: ud["id"] });
          navigate("/courses");
        } else {
          setError("An error occurred while fetching user details.");
        }
      } else {
        const data = await response.json();
        setError(data.error || "Invalid email or password");
      }
    } catch (error) {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: "#0b1329", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Navbar />
      
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 20px",
          background: "radial-gradient(circle at 50% 30%, rgba(37, 99, 235, 0.16) 0%, rgba(11, 19, 41, 1) 75%)",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Animated Orbs */}
        <div
          style={{
            position: "absolute",
            width: "350px",
            height: "350px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #2563eb, #0ea5e9)",
            filter: "blur(100px)",
            opacity: 0.22,
            top: "10%",
            left: "15%",
            animation: "pulse 6s infinite alternate",
            pointerEvents: "none"
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "320px",
            height: "320px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #06b6d4, #3b82f6)",
            filter: "blur(90px)",
            opacity: 0.18,
            bottom: "10%",
            right: "15%",
            animation: "pulse 8s infinite alternate",
            pointerEvents: "none"
          }}
        />

        {/* Login Card */}
        <div
          style={{
            width: "100%",
            maxWidth: "440px",
            background: "rgba(15, 23, 42, 0.8)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "22px",
            padding: "38px 34px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.6)",
            zIndex: 1
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "28px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #2563eb, #0284c7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 14px",
                boxShadow: "0 8px 20px rgba(37, 99, 235, 0.35)"
              }}
            >
              <FontAwesomeIcon icon={faUserCheck} style={{ color: "#ffffff", fontSize: "1.4rem" }} />
            </div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", color: "#ffffff", fontSize: "1.7rem", fontWeight: 800, margin: "0 0 6px" }}>
              Welcome Back
            </h2>
            <p style={{ color: "#94a3b8", fontSize: "0.88rem", margin: 0 }}>
              Sign in to your learning dashboard & courses
            </p>
          </div>

          {error && (
            <div
              style={{
                background: "rgba(239, 68, 68, 0.15)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                color: "#fca5a5",
                padding: "12px 16px",
                borderRadius: "12px",
                fontSize: "0.88rem",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "20px"
              }}
            >
              <FontAwesomeIcon icon={faExclamationCircle} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={login} style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            <div>
              <label style={{ display: "block", color: "#cbd5e1", fontSize: "0.85rem", fontWeight: 600, marginBottom: "8px", textAlign: "left" }}>
                Email Address
              </label>
              <div style={{ position: "relative" }}>
                <FontAwesomeIcon
                  icon={faEnvelope}
                  style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#64748b", fontSize: "0.9rem" }}
                />
                <input
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "13px 16px 13px 44px",
                    background: "rgba(11, 19, 41, 0.7)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    borderRadius: "12px",
                    color: "#ffffff",
                    fontSize: "0.95rem",
                    outline: "none",
                    boxSizing: "border-box",
                    fontFamily: "'Inter', sans-serif"
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: "block", color: "#cbd5e1", fontSize: "0.85rem", fontWeight: 600, marginBottom: "8px", textAlign: "left" }}>
                Password
              </label>
              <div style={{ position: "relative" }}>
                <FontAwesomeIcon
                  icon={faLock}
                  style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#64748b", fontSize: "0.9rem" }}
                />
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "13px 16px 13px 44px",
                    background: "rgba(11, 19, 41, 0.7)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    borderRadius: "12px",
                    color: "#ffffff",
                    fontSize: "0.95rem",
                    outline: "none",
                    boxSizing: "border-box",
                    fontFamily: "'Inter', sans-serif"
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                background: "linear-gradient(135deg, #2563eb 0%, #0284c7 100%)",
                color: "#ffffff",
                border: "none",
                borderRadius: "12px",
                padding: "14px",
                fontSize: "0.98rem",
                fontWeight: 700,
                cursor: loading ? "wait" : "pointer",
                boxShadow: "0 8px 20px rgba(37, 99, 235, 0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                marginTop: "6px",
                transition: "all 0.3s ease"
              }}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Signing in...
                </>
              ) : (
                <>
                  Sign In <FontAwesomeIcon icon={faArrowRight} />
                </>
              )}
            </button>
          </form>

          <div style={{ textAlign: "center", marginTop: "22px", paddingTop: "18px", borderTop: "1px solid rgba(255, 255, 255, 0.08)" }}>
            <span style={{ color: "#94a3b8", fontSize: "0.88rem" }}>
              Don't have an account?{" "}
              <Link to="/register" style={{ color: "#38bdf8", fontWeight: 700, textDecoration: "none" }}>
                Register Here
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
